import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Navbar } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileText, User, Phone } from "lucide-react";
import API from "@/utils/api";

const Enrollment = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [enrollment, setEnrollment] = useState(null);
  const [formData, setFormData] = useState({
    aadhar_card: null,
    birth_certificate: null,
    registration_card: null,
    self_signature: null,
    passport_photo: null,
    whatsapp_number: ""
  });

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const token = localStorage.getItem("token");
    if (!storedUser || !token) {
      navigate("/login");
      return;
    }
    const userData = JSON.parse(storedUser);
    setUser(userData);
    fetchEnrollment(token);
  }, [navigate]);

  const fetchEnrollment = async (token) => {
    try {
      const res = await axios.get(`${API}/enrollment`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.enrollment) {
        setEnrollment(res.data.enrollment);
        setFormData(prev => ({
          ...prev,
          whatsapp_number: res.data.enrollment.whatsapp_number || ""
        }));
      }
    } catch (error) {
      console.error("Error fetching enrollment:", error);
    }
  };

  const handleFileChange = (field, e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert("File size must be less than 5MB");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, [field]: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");
    setLoading(true);
    try {
      await axios.post(`${API}/enrollment`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert("Enrollment documents submitted successfully!");
      navigate("/dashboard");
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to submit enrollment");
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-transparent py-8 px-4" data-testid="enrollment-page">
      <Navbar />
      <div className="max-w-2xl mx-auto pt-20">
        <Card className="rounded-2xl shadow-xl">
          <CardHeader className="text-center border-b">
            <Link to="/" className="flex items-center justify-center gap-2 mb-4">
              <img src="/images/logo.png" alt="NokMechik" className="w-8 h-8 rounded-full object-cover" />
              <span className="text-2xl font-bold text-slate-800">NokMechik</span>
            </Link>
            <CardTitle className="text-2xl">Student Enrollment</CardTitle>
            <CardDescription>Submit your documents to complete enrollment</CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Aadhar Card */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Aadhar Card <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange("aadhar_card", e)}
                  className="rounded-xl"
                  required={!enrollment?.aadhar_card}
                />
                {(formData.aadhar_card || enrollment?.aadhar_card) && (
                  <div className="mt-2">
                    <img src={formData.aadhar_card || enrollment?.aadhar_card} alt="Aadhar" className="h-20 rounded-lg border" />
                  </div>
                )}
              </div>

              {/* Birth Certificate */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Birth Certificate <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange("birth_certificate", e)}
                  className="rounded-xl"
                  required={!enrollment?.birth_certificate}
                />
                {(formData.birth_certificate || enrollment?.birth_certificate) && (
                  <div className="mt-2">
                    <img src={formData.birth_certificate || enrollment?.birth_certificate} alt="Birth Certificate" className="h-20 rounded-lg border" />
                  </div>
                )}
              </div>

              {/* Registration Card */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Registration Card <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange("registration_card", e)}
                  className="rounded-xl"
                  required={!enrollment?.registration_card}
                />
                {(formData.registration_card || enrollment?.registration_card) && (
                  <div className="mt-2">
                    <img src={formData.registration_card || enrollment?.registration_card} alt="Registration Card" className="h-20 rounded-lg border" />
                  </div>
                )}
              </div>

              {/* Self Signature */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Self Signature <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange("self_signature", e)}
                  className="rounded-xl"
                  required={!enrollment?.self_signature}
                />
                {(formData.self_signature || enrollment?.self_signature) && (
                  <div className="mt-2">
                    <img src={formData.self_signature || enrollment?.self_signature} alt="Signature" className="h-20 rounded-lg border" />
                  </div>
                )}
              </div>

              {/* Passport Photo */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Passport Size Photo <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange("passport_photo", e)}
                  className="rounded-xl"
                  required={!enrollment?.passport_photo}
                />
                {(formData.passport_photo || enrollment?.passport_photo) && (
                  <div className="mt-2">
                    <img src={formData.passport_photo || enrollment?.passport_photo} alt="Photo" className="h-20 rounded-lg border" />
                  </div>
                )}
              </div>

              {/* WhatsApp Number */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  WhatsApp Number <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="tel"
                  placeholder="Enter WhatsApp number"
                  value={formData.whatsapp_number}
                  onChange={(e) => setFormData({ ...formData, whatsapp_number: e.target.value })}
                  className="rounded-xl"
                  required
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-teal-600 hover:bg-teal-700 rounded-full py-6"
                disabled={loading}
              >
                {loading ? "Submitting..." : "Submit Enrollment"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Enrollment;
